import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAlerts } from '../../hooks/useAlerts';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useIdentity } from '../../auth/useIdentity';
import { authDisabled, useAuth } from '../../auth/useAuth';
import { Follow, getFollows } from '../data/cockpit';
import { envKind, useCockpit } from '../data/CockpitContext';
import { useFirstLoads, usePoll, useNow } from '../data/usePoll';
import { ago, when } from '../data/format';
import { EnvChip, Icon, Mark, Spinner } from '../components/ui';
import { v2 } from '../paths';
import { SECTION_LABEL, sameTab, useScope } from './scope';
import { SearchPalette } from './SearchPalette';
import { ScreenBoundary } from '../components/ErrorBoundary';

const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'nav on' : 'nav');
const subClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'nav sub on' : 'nav sub');

/** A menu under a top-bar button that closes on a click elsewhere and on Esc. */
const Popover = ({ button, children, width = 380 }: { button: (toggle: () => void, open: boolean) => ReactNode; children: (close: () => void) => ReactNode; width?: number }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <span ref={ref} style={{ position: 'relative' }}>
            {button(() => setOpen((o) => !o), open)}
            {open && (
                <div className="card pop" style={{ width }}>
                    {children(() => setOpen(false))}
                </div>
            )}
        </span>
    );
};

/** The rail: the same sections everywhere, the environment you are in as a box of its own. */
const Rail = () => {
    const { environments, isAdmin, find, lab } = useCockpit();
    const scope = useScope();
    const [last, setLast] = useState<{ customer: string; environment: string }>();

    useEffect(() => {
        if (scope.customer && scope.environment) setLast({ customer: scope.customer, environment: scope.environment });
    }, [scope.customer, scope.environment]);

    const totals = useMemo(
        () => ({
            us: environments.reduce((n, e) => n + e.waitingOnUs, 0),
            open: environments.reduce((n, e) => n + e.open, 0),
            watching: environments.filter((e) => e.watchStatus === 'watching').length,
        }),
        [environments]
    );

    const here = last ? find(last.customer, last.environment) : undefined;

    if (!isAdmin) {
        return (
            <aside className="rail" aria-label="Navigatie">
                <div className="navh">Foutenlogboek</div>
                {environments.map((e) => (
                    <NavLink key={`${e.customer}/${e.environment}`} className={navClass} to={v2.errorLog(e.customer, e.environment)}>
                        <span>
                            <Icon name="log" />
                            {e.customer}
                        </span>
                        <EnvChip kind={envKind(e)} environment={e.environment} />
                    </NavLink>
                ))}
            </aside>
        );
    }

    return (
        <aside className="rail" aria-label="Navigatie">
            <div className="navh">Overzicht</div>
            <NavLink end className={navClass} to={v2.home()}>
                <span>
                    <Icon name="dashboard" />
                    Dashboard
                </span>
                {totals.us > 0 && <span className="cnt red">{totals.us}</span>}
            </NavLink>
            <NavLink className={navClass} to={v2.environments()}>
                <span>
                    <Icon name="envs" />
                    Omgevingen
                </span>
                <span className="cnt">{environments.length}</span>
            </NavLink>
            <NavLink end className={navClass} to={v2.issues()}>
                <span>
                    <Icon name="issues" />
                    Issues
                </span>
                <span className="cnt">{totals.open}</span>
            </NavLink>
            <NavLink className={navClass} to={v2.deploy()}>
                <span>
                    <Icon name="deploy" />
                    Deploy
                </span>
                {totals.watching > 0 && <span className="cnt amb">{totals.watching} live</span>}
            </NavLink>
            <NavLink className={navClass} to={v2.exact()}>
                <span>
                    <Icon name="exact" />
                    Exact-verbruik
                </span>
            </NavLink>

            {here && (
                <div className="scopebox">
                    <div className="navh">
                        <span>{scope.customer ? '' : 'Laatst · '}{here.customer}</span>
                        <EnvChip kind={envKind(here)} environment={here.environment} />
                    </div>
                    <NavLink end className={subClass} to={v2.env(here.customer, here.environment)}>
                        <span>Live</span>
                    </NavLink>
                    <NavLink className={subClass} to={v2.envIssues(here.customer, here.environment)}>
                        <span>Issues</span>
                        <span className="cnt">{here.open ?? 0}</span>
                    </NavLink>
                    <NavLink className={subClass} to={v2.errorLog(here.customer, here.environment)}>
                        <span>Foutenlogboek</span>
                    </NavLink>
                    <NavLink className={subClass} to={v2.monitor(here.customer, here.environment)}>
                        <span>Monitor</span>
                        {here.watchStatus === 'watching' && <span className="cnt amb">live</span>}
                    </NavLink>
                    <NavLink className={subClass} to={v2.envExact(here.customer, here.environment)}>
                        <span>Exact</span>
                    </NavLink>
                </div>
            )}

            <div className="navh">
                <span>Lab</span>
                {lab && <EnvChip kind="lab" environment={`${lab.customer} ${lab.environment}`} />}
            </div>
            <NavLink className={navClass} to={v2.scenarios()}>
                <span>
                    <Icon name="lab" />
                    Scenario's
                </span>
            </NavLink>

            <div className="navh">Leren</div>
            <NavLink end className={navClass} to={v2.learn()}>
                <span>
                    <Icon name="unknown" />
                    Onbekende fouten
                </span>
            </NavLink>
            <NavLink end className={navClass} to={v2.learn('mails')}>
                <span>
                    <Icon name="mail" />
                    Klantmails
                </span>
            </NavLink>

            <div className="navh">Oud dashboard</div>
            <Link className="nav" to="/">
                <span>
                    <Icon name="back" />
                    Terug naar v1
                </span>
            </Link>
        </aside>
    );
};

/** Klant / Omgeving / onderdeel: always visible, and every part opens a way to switch. */
const ScopeBar = ({ onSwitch }: { onSwitch: () => void }) => {
    const scope = useScope();
    const { ofCustomer, find, isAdmin } = useCockpit();
    const env = find(scope.customer, scope.environment);
    const siblings = ofCustomer(scope.customer);
    const kind = env ? envKind(env) : undefined;

    const target = (environment: string) =>
        scope.inEnv
            ? sameTab(scope, scope.customer!, environment)
            : scope.section === 'exact'
              ? v2.exact(scope.customer!, environment)
              : v2.env(scope.customer!, environment);

    return (
        <nav className="crumbs" aria-label="Waar ben je">
            <button className="crumb" onClick={onSwitch} disabled={!isAdmin}>
                <span className="k">
                    <small>Klant</small>
                    <span>{scope.customer ?? 'Alle klanten'}</span>
                </span>
                {isAdmin && <Icon name="chevron" size={12} />}
            </button>
            <span className="sep">/</span>
            {scope.customer && siblings.length > 0 ? (
                <div className="seg" role="group" aria-label={`Omgeving van ${scope.customer}`}>
                    {siblings.map((s) => (
                        <Link
                            key={s.environment}
                            to={target(s.environment)}
                            className={s.environment.toLowerCase() === (scope.environment ?? '').toLowerCase() ? `on ${envKind(s)}` : ''}
                        >
                            {s.environment.toUpperCase()}
                        </Link>
                    ))}
                </div>
            ) : (
                <Link className={`crumb ${kind ?? ''}`} to={v2.environments()}>
                    <span className="k">
                        <small>Omgeving</small>
                        <span>{scope.environment ? scope.environment.toUpperCase() : 'Alle omgevingen'}</span>
                    </span>
                </Link>
            )}
            {scope.section !== 'home' && !scope.inEnv && (
                <>
                    <span className="sep">/</span>
                    <span className="crumb" style={{ cursor: 'default' }}>
                        <span className="k">
                            <small>Onderdeel</small>
                            <span>{SECTION_LABEL[scope.section]}</span>
                        </span>
                    </span>
                </>
            )}
            {(scope.issueKey || scope.objectId) && (
                <>
                    <span className="sep">/</span>
                    <span className="crumb" style={{ cursor: 'default' }}>
                        <span className="k">
                            <small>Object</small>
                            <span className="mono">{scope.objectId ?? scope.issueKey!.split('|').pop()}</span>
                        </span>
                    </span>
                </>
            )}
        </nav>
    );
};

const FollowsButton = () => {
    const axios = useAxiosSwagger();
    const navigate = useNavigate();
    const poll = usePoll<Follow[]>(axios ? (signal) => getFollows(axios, signal) : null, 15000, [axios]);
    const follows = poll.data ?? [];
    const busy = follows.filter((f) => f.state.exact !== 'ok').length;

    return (
        <Popover
            button={(toggle) => (
                <button className="ibtn" aria-label="Gevolgde objecten" onClick={toggle}>
                    <Icon name="pin" />
                    {busy > 0 && <span className="badge blue">{busy}</span>}
                </button>
            )}
        >
            {(close) => (
                <>
                    <div className="ch">
                        <span className="ct">Gevolgd</span>
                        <span className="mut" style={{ fontSize: 12 }}>tot het in Exact staat</span>
                    </div>
                    {poll.error && <div className="empty">{poll.error}</div>}
                    {!poll.error && follows.length === 0 && <div className="empty">Je volgt nog niets. Volg een object vanuit een issue.</div>}
                    {follows.map((f) => (
                        <button
                            key={f.id}
                            className="li"
                            style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}
                            onClick={() => {
                                close();
                                if (f.state.issueKey) navigate(v2.issue(f.customer, f.environment, f.state.issueKey));
                                else navigate(v2.env(f.customer, f.environment));
                            }}
                        >
                            <span>
                                <span className="t mono" style={{ fontWeight: 500 }}>{f.label}</span>
                                <span className="m">
                                    {f.customer} {f.environment}
                                </span>
                            </span>
                            <span className="hrow" style={{ gap: 4 }}>
                                <Mark tone={f.state.chargebee === 'todo' ? 'todo' : f.state.chargebee} />
                                <Mark tone={f.state.connector === 'todo' ? 'todo' : f.state.connector} />
                                <Mark tone={f.state.exact === 'todo' ? 'todo' : f.state.exact} />
                            </span>
                        </button>
                    ))}
                </>
            )}
        </Popover>
    );
};

const AlertsButton = () => {
    const { alerts, unread, markSeen } = useAlerts();
    const navigate = useNavigate();

    return (
        <Popover
            width={400}
            button={(toggle) => (
                <button
                    className="ibtn"
                    aria-label="Meldingen"
                    onClick={() => {
                        toggle();
                        markSeen();
                    }}
                >
                    <Icon name="bell" />
                    {unread > 0 && <span className="badge">{unread}</span>}
                </button>
            )}
        >
            {(close) => (
                <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                    <div className="ch">
                        <span className="ct">Meldingen</span>
                    </div>
                    {alerts.length === 0 && <div className="empty">Geen meldingen.</div>}
                    {alerts.slice(0, 20).map((a) => (
                        <button
                            key={a.id}
                            className="li"
                            onClick={() => {
                                close();
                                if (a.customer && a.environment) navigate(v2.envIssues(a.customer, a.environment));
                            }}
                        >
                            <span className={`sev ${a.severity === 'Error' ? 'us' : a.severity === 'Warning' ? 'cu' : 'au'}`} />
                            <span>
                                <span className="t" style={{ fontWeight: 500 }}>{a.title}</span>
                                <span className="m">
                                    {a.customer} {a.environment}
                                </span>
                            </span>
                            <span className="mono mut" style={{ fontSize: 12 }}>{when(a.createdAt)}</span>
                        </button>
                    ))}
                </div>
            )}
        </Popover>
    );
};

const initials = (name?: string | null) =>
    (name ?? '')
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]!.toUpperCase())
        .join('') || '?';

/** Who is signed in, with what rights, and the way out. */
const ProfileMenu = () => {
    const { user, logout } = useAuth();
    const { identity, isAdmin } = useIdentity();
    const name = identity?.name || user?.name || user?.email;
    const roles = identity?.roles ?? [];

    return (
        <Popover
            width={300}
            button={(toggle, open) => (
                <button className={`avatar ${open ? 'on' : ''}`} aria-label="Profiel" onClick={toggle}>
                    {name ? initials(name) : <Icon name="user" />}
                </button>
            )}
        >
            {() => (
                <>
                    <div className="cb" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <b>{name ?? 'Onbekend'}</b>
                        {user?.email && user.email !== name && <span className="mut" style={{ fontSize: 13 }}>{user.email}</span>}
                        <span className="hrow" style={{ gap: 6, marginTop: 6 }}>
                            {isAdmin ? <span className="chip info">Support</span> : roles.map((r) => <span key={r} className="chip">{r}</span>)}
                            {authDisabled && <span className="chip warn">Auth0 uit · alleen lezen</span>}
                        </span>
                    </div>
                    <div className="ch" style={{ borderTop: '1px solid #1A212B', borderBottom: 0 }}>
                        <Link className="btn sm ghost" to="/">
                            <Icon name="back" size={14} />
                            Oud dashboard
                        </Link>
                        <button
                            className="btn sm danger"
                            disabled={authDisabled}
                            title={authDisabled ? 'Lokaal staat Auth0 uit: er is geen sessie om uit te loggen' : undefined}
                            onClick={() => logout({ returnTo: window.location.origin })}
                        >
                            <Icon name="logout" size={14} />
                            Uitloggen
                        </button>
                    </div>
                </>
            )}
        </Popover>
    );
};

const LiveIndicator = () => {
    const { loadedAt, error } = useCockpit();
    const loads = useFirstLoads();
    const now = useNow(1000);
    const age = loadedAt ? (now - loadedAt.getTime()) / 1000 : undefined;

    if (loads > 0) {
        return (
            <span className="live busy" title={`${loads} onderdelen laden voor het eerst`}>
                <Spinner />
                Laden…
            </span>
        );
    }

    const cls = error && !loadedAt ? 'live down' : age !== undefined && age > 60 ? 'live stale' : 'live';
    const label = error && !loadedAt ? 'Geen verbinding' : loadedAt ? `Live · ${ago(loadedAt, now)}` : 'Verbinden…';

    return (
        <span className={cls} title={error ?? undefined}>
            <span className="dot" />
            {label}
        </span>
    );
};

const BAND_TEXT = { prod: 'Productie', test: 'Testomgeving', lab: 'Lab · scenariotests' } as const;

/** The colour of the environment you are in, named, under the top bar on every screen of it. */
const Band = () => {
    const scope = useScope();
    const { find } = useCockpit();
    const loads = useFirstLoads();
    const env = find(scope.customer, scope.environment);
    const kind = env ? envKind(env) : scope.environment ? (scope.environment.toLowerCase().startsWith('prod') ? 'prod' : 'test') : undefined;

    return (
        <div className={`band ${kind ?? ''}`}>
            {kind && scope.customer && (
                <span>
                    {BAND_TEXT[kind]} · {scope.customer} {scope.environment}
                    {kind === 'prod' && <span className="hint"> · echte klantdata</span>}
                </span>
            )}
            <i className={`loadbar ${loads > 0 ? 'on' : ''}`} />
        </div>
    );
};

/** The v2 frame around every page: scope bar, band in the colour of the environment, rail and palette. */
export const Shell = () => {
    const { isAdmin } = useCockpit();
    const [palette, setPalette] = useState<false | 'search' | 'switch'>(false);

    useEffect(() => {
        document.body.classList.add('v2-body');
        return () => document.body.classList.remove('v2-body');
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setPalette('search');
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isAdmin]);

    return (
        <div className="v2">
            <div className="topwrap">
                <header className="top">
                    <Link className="brand" to={isAdmin ? v2.home() : '/v2'}>
                        <b>c2e</b>
                        {isAdmin ? 'Dashboard' : 'Koppeling Chargebee · Exact'}
                    </Link>
                    <ScopeBar onSwitch={() => setPalette('switch')} />
                    {isAdmin && (
                        <button className="search" onClick={() => setPalette('search')}>
                            <Icon name="search" size={14} />
                            <span className="grow" style={{ textAlign: 'left' }}>Factuur, betaling, klant…</span>
                            <span className="kbd">Ctrl K</span>
                        </button>
                    )}
                    {isAdmin && <FollowsButton />}
                    {isAdmin && <AlertsButton />}
                    <LiveIndicator />
                    <ProfileMenu />
                </header>
                <Band />
            </div>
            <div className="shell">
                <Rail />
                <main className="main">
                    <ScreenBoundary>
                        <Outlet />
                    </ScreenBoundary>
                </main>
            </div>
            {palette && <SearchPalette mode={palette} onMode={setPalette} onClose={() => setPalette(false)} />}
        </div>
    );
};
